/**
 * Admin — Excelior Training Intake.
 *
 * Per major page, Michael answers 3 questions:
 *   1. What is this page?
 *   2. What action should the user take?
 *   3. What benefit will they experience?
 *
 * Answers live in Mongo collection `excelior_training` keyed by page_id.
 * When a user asks Excelior something, the backend looks up the route
 * they came from and injects Michael's answers into the system prompt —
 * so Excelior gives precise, page-specific walkthroughs instead of
 * generic wellness-speak.
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, Save, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DRAFT_KEY = "excelior_training_drafts_v1";

function loadDrafts() {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");
  } catch (_) { return {}; }
}
function saveDraft(pageId, entry) {
  try {
    const all = loadDrafts();
    all[pageId] = { ...entry, _t: Date.now() };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(all));
  } catch (_) {}
}
function clearDraft(pageId) {
  try {
    const all = loadDrafts();
    delete all[pageId];
    localStorage.setItem(DRAFT_KEY, JSON.stringify(all));
  } catch (_) {}
}

export default function AdminExceliorTraining() {
  const [groups, setGroups] = useState({});
  const [stats, setStats] = useState({ total: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [errorId, setErrorId] = useState(null);
  const [errorDetail, setErrorDetail] = useState("");
  const [me, setMe] = useState(null);

  // Pull current user once so we can warn if they're not admin
  useEffect(() => {
    fetch(`${API}/api/auth/me`, { headers: { ...auth() } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setMe)
      .catch(() => {});
  }, []);

  const isAdmin = me?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/excelior/training`, { headers: { ...auth() } });
      const data = await r.json();
      // Merge any local drafts on top of server data so content persists
      // across failed-save sessions / token expiry / role switches.
      const drafts = loadDrafts();
      const merged = {};
      for (const [group, pages] of Object.entries(data.groups || {})) {
        merged[group] = pages.map((p) => {
          const d = drafts[p.page_id];
          if (!d) return p;
          // Draft wins ONLY on fields it has non-empty content for.
          return {
            ...p,
            what_is_it: d.what_is_it || p.what_is_it,
            user_action: d.user_action || p.user_action,
            user_benefit: d.user_benefit || p.user_benefit,
            example_response: d.example_response || p.example_response,
            _hasDraft: true,
          };
        });
      }
      setGroups(merged);
      setStats({ total: data.total || 0, completed: data.completed || 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (group, pageId, field, value) => {
    setGroups((g) => {
      const next = { ...g };
      next[group] = next[group].map((p) => {
        if (p.page_id !== pageId) return p;
        const updated = { ...p, [field]: value, _dirty: true };
        // Persist a local draft so nothing is ever lost on save-failure,
        // session change, network blip, token expiry, or tab reload.
        saveDraft(pageId, {
          what_is_it: updated.what_is_it || "",
          user_action: updated.user_action || "",
          user_benefit: updated.user_benefit || "",
          example_response: updated.example_response || "",
        });
        return updated;
      });
      return next;
    });
  };

  const save = async (page) => {
    if (!isAdmin) {
      setErrorId(page.page_id);
      setErrorDetail("Admin required — your answers are safely drafted on this device until you log in as admin.");
      setTimeout(() => { setErrorId(null); setErrorDetail(""); }, 6000);
      return;
    }
    setSavingId(page.page_id);
    setErrorId(null);
    setErrorDetail("");
    try {
      const r = await fetch(`${API}/api/excelior/training/${page.page_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          what_is_it: page.what_is_it || "",
          user_action: page.user_action || "",
          user_benefit: page.user_benefit || "",
          example_response: page.example_response || "",
        }),
      });
      if (!r.ok) {
        let msg = `Save failed (${r.status})`;
        try {
          const j = await r.json();
          if (j.detail) msg = `${msg} — ${j.detail}`;
        } catch (_) {}
        throw new Error(msg);
      }
      // Success: clear the local draft since it's now on the server.
      clearDraft(page.page_id);
      setSavedId(page.page_id);
      setTimeout(() => setSavedId(null), 2200);
      // Re-compute completion stat without full reload
      setGroups((g) => {
        const next = { ...g };
        next[page.group] = next[page.group].map((p) =>
          p.page_id === page.page_id ? { ...p, _dirty: false } : p,
        );
        return next;
      });
      // Recount completed
      const all = Object.values({ ...groups }).flat();
      const completed = all.filter((p) =>
        p.page_id === page.page_id
          ? (page.what_is_it && page.user_action)
          : (p.what_is_it && p.user_action),
      ).length;
      setStats((s) => ({ ...s, completed }));
    } catch (e) {
      setErrorId(page.page_id);
      setErrorDetail(e.message || "Save failed — retry");
      setTimeout(() => { setErrorId(null); setErrorDetail(""); }, 6000);
    } finally {
      setSavingId(null);
    }
  };

  const progressPct = useMemo(() => {
    if (!stats.total) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  }, [stats]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0E1A2C] via-[#0A1424] to-[#060B16] text-white">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-[#0A1424]/75 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link to="/admin" className="p-2 -ml-2 rounded-lg hover:bg-white/5" data-testid="excelior-training-back">
            <ArrowLeft size={20} className="text-white/55" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-white/55 text-[10px] tracking-[0.3em] uppercase">
              <Sparkles size={12} className="text-amber-300/70" />
              <span>Excelior Training</span>
            </div>
            <h1 className="text-white/90 text-xl font-semibold" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
              Teach Excelior every page
            </h1>
          </div>
          <div className="text-right">
            <div className="text-white/85 text-sm font-medium" data-testid="excelior-training-progress">
              {stats.completed} / {stats.total}
            </div>
            <div className="text-white/40 text-[10px] uppercase tracking-[0.22em]">{progressPct}% complete</div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-5 pb-3">
          <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6 pb-32 space-y-10" data-testid="excelior-training-page">
        {/* Admin gate banner — only saves if logged in as admin */}
        {me && !isAdmin && (
          <div
            className="rounded-2xl p-5 backdrop-blur-md sticky top-20 z-30"
            data-testid="excelior-training-not-admin"
            style={{ background: "rgba(220,80,90,0.18)", border: "1px solid rgba(220,80,90,0.55)", boxShadow: "0 18px 40px -16px rgba(220,80,90,0.4)" }}
          >
            <p className="text-rose-100 text-sm font-semibold">
              Signed in as <b>{me.email}</b> — saves are disabled for the <b>{me.role || "user"}</b> role.
            </p>
            <p className="text-rose-100/75 text-xs mt-1.5 leading-relaxed">
              Your typed answers are <b>saved locally</b> on this device — nothing is lost. Log in as <b>michael@reclaim.ai</b> to push them to the server.
            </p>
            <button
              onClick={() => {
                try { localStorage.removeItem("reclaim_token"); } catch (_) {}
                window.location.href = "/login?redirect=/admin/excelior-training";
              }}
              className="mt-3 px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase text-rose-50 bg-rose-500/30 hover:bg-rose-500/45 border border-rose-200/50 transition-colors"
              data-testid="excelior-switch-admin"
            >
              Switch to admin login
            </button>
          </div>
        )}

        {/* Intro card */}
        <div className="rounded-2xl p-6 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-white/75 text-sm leading-relaxed">
            For every page in the app, answer three short questions in your own words. Excelior will use your answers to give users pin-point guidance on whatever screen they&apos;re on. You can come back and edit anytime. The more honest and specific you are, the more magical Excelior feels.
          </p>
          <ul className="mt-4 space-y-1.5 text-white/55 text-sm">
            <li>• <b className="text-white/80">What is it?</b> One or two lines describing the page&apos;s purpose.</li>
            <li>• <b className="text-white/80">What should they do?</b> The single next action a user should take.</li>
            <li>• <b className="text-white/80">What&apos;s the benefit?</b> The felt outcome they get from taking that action.</li>
            <li>• <b className="text-white/80">Example phrase (optional):</b> A canned response Excelior can adapt.</li>
          </ul>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-white/50">
            <Loader2 className="animate-spin" size={18} />
            <span className="ml-2 text-sm">Loading pages…</span>
          </div>
        ) : (
          Object.entries(groups).map(([groupName, pages]) => (
            <section key={groupName} className="space-y-4">
              <h2 className="text-amber-300/80 text-[10px] tracking-[0.3em] uppercase font-medium pl-1">
                {groupName}
              </h2>
              {pages.map((p) => (
                <div
                  key={p.page_id}
                  className="rounded-2xl p-5 backdrop-blur-md"
                  style={{
                    background: "rgba(255,255,255,0.035)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                  data-testid={`excelior-training-card-${p.page_id}`}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-white/92 text-lg font-medium" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                        {p.label}
                      </h3>
                      <p className="text-white/35 text-[11px] mt-0.5 tracking-wide font-mono">{p.route}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.what_is_it && p.user_action && !p._dirty && (
                        <span className="inline-flex items-center gap-1 text-emerald-300/80 text-[11px] tracking-wider uppercase">
                          <CheckCircle2 size={12} /> Ready
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Field
                      label="What is this page?"
                      placeholder="e.g. A visualisation tool where users deposit 'wins' into their abundance bank."
                      value={p.what_is_it}
                      onChange={(v) => updateField(groupName, p.page_id, "what_is_it", v)}
                      testId={`field-what-${p.page_id}`}
                    />
                    <Field
                      label="What action should they take?"
                      placeholder="e.g. Tap the + button and type one thing they received today — big or small."
                      value={p.user_action}
                      onChange={(v) => updateField(groupName, p.page_id, "user_action", v)}
                      testId={`field-action-${p.page_id}`}
                    />
                    <Field
                      label="What benefit will they experience?"
                      placeholder="e.g. It rewires their subconscious to notice abundance in ordinary moments."
                      value={p.user_benefit}
                      onChange={(v) => updateField(groupName, p.page_id, "user_benefit", v)}
                      testId={`field-benefit-${p.page_id}`}
                    />
                    <Field
                      label="Example phrase Excelior can adapt (optional)"
                      placeholder="e.g. 'You're looking at your Bank of Reclaim. Tap the plus sign, log one win from today — that simple act teaches your nervous system to expect more.'"
                      value={p.example_response}
                      onChange={(v) => updateField(groupName, p.page_id, "example_response", v)}
                      testId={`field-example-${p.page_id}`}
                      multiline
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-end gap-2">
                    {errorId === p.page_id && (
                      <span className="text-rose-300/85 text-xs max-w-[230px] text-right">
                        {errorDetail || "Save failed — retry"}
                      </span>
                    )}
                    <button
                      onClick={() => save(p)}
                      disabled={savingId === p.page_id}
                      data-testid={`save-${p.page_id}`}
                      className="px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase text-white/90 transition-all active:scale-[0.97] disabled:opacity-60 flex items-center gap-2"
                      style={{
                        background: savedId === p.page_id
                          ? "linear-gradient(180deg, rgba(110,190,130,0.22), rgba(70,140,90,0.18))"
                          : "linear-gradient(180deg, rgba(217,180,120,0.22), rgba(180,140,80,0.18))",
                        border: savedId === p.page_id
                          ? "1px solid rgba(110,190,130,0.5)"
                          : "1px solid rgba(217,180,120,0.5)",
                      }}
                    >
                      {savingId === p.page_id ? (
                        <><Loader2 size={12} className="animate-spin" /> Saving…</>
                      ) : savedId === p.page_id ? (
                        <><CheckCircle2 size={12} /> Saved</>
                      ) : (
                        <><Save size={12} /> Save</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </section>
          ))
        )}
      </main>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, testId, multiline }) {
  return (
    <label className="block">
      <span className="block text-white/50 text-[11px] tracking-[0.18em] uppercase mb-1.5">{label}</span>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full px-3.5 py-2.5 rounded-lg text-white/90 text-sm bg-black/30 border border-white/10 focus:border-amber-300/40 focus:outline-none resize-y transition-colors"
          data-testid={testId}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3.5 py-2.5 rounded-lg text-white/90 text-sm bg-black/30 border border-white/10 focus:border-amber-300/40 focus:outline-none transition-colors"
          data-testid={testId}
        />
      )}
    </label>
  );
}
